import React, { useMemo, memo } from 'react';
import {
  SmartKeyMetricCardProps,
  getCardMetricType,
  getCardTitle
} from 'src/components/BeaconV2Components/KeyMetricCards/SmartKeyMetricCard';
import useForecastMetricByWeekIdV2 from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useForecastMetricByWeekIdV2';
import MetricSummaryTrendCardLoading from 'src/components/BeaconV2Components/MetricSummaryTrendCard/MetricSummaryTrendCardLoading';
import MetricSummaryTrendCardV2 from 'src/components/BeaconV2Components/MetricSummaryTrendCard/MetricSummaryTrendCardV2';
import { getAppName, updateUrlQueryParams } from 'src/utils/app';
import { useHistory, useQueryParamValue } from 'src/utils/Hooks';
import useForecastDerivedMetricByWeekId from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useForecastDerivedMetricByWeekId';
import { useForecastComparisonPeriod } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/hooks';
import { ForecastComparisonPeriod } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/types';
import { INDEX_FIELDS } from 'src/utils/entityDefinitions';
import { timestampToWeekId } from 'src/utils/dateUtils';
import { SplineChartV2Series } from 'src/components/BeaconV2Components/SplineChartV2';

interface SmartForecastKeyMetricCardProps extends SmartKeyMetricCardProps {
  children?: React.ReactNode;
  loading?: boolean;
}

/**
 * Key metric card for derived metrics (e.g., purchase rate)
 * Still uses V1 hook but converts data to V2 format
 */
function DerivedForecastKeyMetricCard({
  indexName,
  fieldName,
  variant,
  title,
  loading,
  ...rest
}: SmartKeyMetricCardProps) {
  const {
    isLoading: forecastLoading,
    primaryData,
    secondaryData,
    formattedPrimaryTotal,
    formattedPercentChange,
    percentChange,
    forecastData
  } = useForecastDerivedMetricByWeekId({ indexName, fieldName, ...rest });
  const history = useHistory();
  const selectedIndex = useQueryParamValue('indexName', 'sales');
  const selectedField = useQueryParamValue('keyMetric', 'retailSales');
  const forecastComparisonPeriod = useForecastComparisonPeriod();
  const isPriorPeriod = forecastComparisonPeriod === ForecastComparisonPeriod.PRIOR_PERIOD;

  // Convert V1 data format to V2 series format
  const primarySeries: SplineChartV2Series = useMemo(
    () => ({
      id: `${indexName}-${fieldName}-primary`,
      name: 'Primary',
      data: primaryData.map(([timestamp, value]: [number, number]) => ({
        weekId: timestampToWeekId(timestamp),
        value
      }))
    }),
    [indexName, fieldName, primaryData]
  );

  const comparisonSeries: SplineChartV2Series = useMemo(
    () => ({
      id: `${indexName}-${fieldName}-comparison`,
      name: 'Comparison',
      data: secondaryData.map(([timestamp, value]: [number, number]) => ({
        weekId: timestampToWeekId(timestamp),
        value
      }))
    }),
    [indexName, fieldName, secondaryData]
  );

  const forecastSeries: SplineChartV2Series = useMemo(
    () => ({
      id: `${indexName}-${fieldName}-forecast`,
      name: 'Forecast',
      data: (forecastData || []).map(([timestamp, value]: [number, number]) => ({
        weekId: timestampToWeekId(timestamp),
        value
      })),
      isDashed: true
    }),
    [indexName, fieldName, forecastData]
  );

  return forecastLoading || loading ? (
    <MetricSummaryTrendCardLoading variant={variant} />
  ) : (
    <MetricSummaryTrendCardV2
      variant={variant}
      title={title || getCardTitle(indexName, fieldName)}
      primarySeries={primarySeries}
      comparisonSeries={comparisonSeries}
      forecastSeries={forecastSeries}
      metricType={getCardMetricType(indexName, fieldName)}
      value={formattedPrimaryTotal}
      change={formattedPercentChange}
      direction={percentChange > 0 ? 'increasing' : 'decreasing'}
      onClick={() => {
        history.push(
          updateUrlQueryParams({
            keyMetric: fieldName,
            indexName
          })
        );
      }}
      selected={selectedIndex === indexName && selectedField === fieldName}
      isPriorPeriodOverride={isPriorPeriod}
      {...rest}
    />
  );
}

/**
 * Key metric card for all other metrics using V2 hooks
 */
function ForecastKeyMetricCard({ indexName, fieldName, variant, title, loading, ...rest }: SmartKeyMetricCardProps) {
  const {
    isLoading: forecastLoading,
    primarySeries,
    comparisonSeries,
    forecastSeries,
    formattedPrimaryTotal,
    formattedPercentChange,
    percentChange
  } = useForecastMetricByWeekIdV2({ indexName, fieldName });
  const history = useHistory();
  const selectedIndex = useQueryParamValue('indexName', 'sales');
  const selectedField = useQueryParamValue('keyMetric', 'retailSales');
  const forecastComparisonPeriod = useForecastComparisonPeriod();
  const isPriorPeriod = forecastComparisonPeriod === ForecastComparisonPeriod.PRIOR_PERIOD;

  return forecastLoading || loading ? (
    <MetricSummaryTrendCardLoading variant={variant} />
  ) : (
    <MetricSummaryTrendCardV2
      variant={variant}
      title={title || getCardTitle(indexName, fieldName)}
      primarySeries={primarySeries}
      comparisonSeries={comparisonSeries}
      forecastSeries={forecastSeries}
      metricType={getCardMetricType(indexName, fieldName)}
      value={formattedPrimaryTotal}
      change={formattedPercentChange}
      direction={percentChange > 0 ? 'increasing' : 'decreasing'}
      onClick={() => {
        history.push(
          updateUrlQueryParams({
            keyMetric: fieldName,
            indexName
          })
        );
      }}
      selected={selectedIndex === indexName && selectedField === fieldName}
      isPriorPeriodOverride={isPriorPeriod}
      {...rest}
    />
  );
}

/**
 * Renders either normal or derived key metric card
 */
export default memo(function SmartForecastKeyMetricCard(args: SmartForecastKeyMetricCardProps) {
  const { indexName, fieldName } = args;
  const field = useMemo(() => INDEX_FIELDS.getField(getAppName(), indexName, fieldName), [indexName, fieldName]);

  if (field.aggregationFunctionType === 'derived') {
    return <DerivedForecastKeyMetricCard {...args} />;
  }

  return <ForecastKeyMetricCard {...args} />;
});
